/**
 * Small shared pieces for the host dashboard tabs (README §3) — the bordered text
 * inputs, the square avatar with the tint, the counter line. Everything on the kit.
 */
import React from 'react';
import TintSquare from '../hand/TintSquare';
import { MUTED, SUBTLE, DANGER } from '../surface';

export function Avatar({ name, imageUrl, size = 40, tintKey }) {
  return <TintSquare name={name} imageUrl={imageUrl} size={size} tintKey={tintKey || name} />;
}

const FIELD = 'w-full border-2 border-black bg-white font-mono text-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export function HostInput({ className = '', style = {}, ...rest }) {
  return <input className={`${FIELD} ${className}`} style={{ padding: '11px 12px', fontSize: 14, ...style }} {...rest} />;
}

export function HostTextarea({ className = '', style = {}, ...rest }) {
  return <textarea className={`${FIELD} resize-none ${className}`} style={{ padding: 12, fontSize: 14, lineHeight: 1.6, ...style }} {...rest} />;
}

export function HostSelect({ className = '', style = {}, children, ...rest }) {
  return <select className={`${FIELD} ${className}`} style={{ padding: '11px 12px', fontSize: 14, ...style }} {...rest}>{children}</select>;
}

/** `0 of 2000 characters` — turns red at the cap. */
export function Counter({ n, max }) {
  return <span className="font-mono" style={{ fontSize: 11, color: n >= max ? DANGER : MUTED }}>{n} of {max} characters</span>;
}

export function Help({ children, style = {} }) {
  return <span className="font-mono" style={{ fontSize: 10, color: MUTED, lineHeight: 1.5, ...style }}>{children}</span>;
}

export function Meta({ children, subtle = false, style = {}, className = '' }) {
  return <div className={`font-mono ${className}`} style={{ fontSize: 11, color: subtle ? SUBTLE : MUTED, ...style }}>{children}</div>;
}

export function ErrorLine({ children }) {
  if (!children) return null;
  return <div className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: DANGER }}>{children}</div>;
}

/** Hand-rolled scan-frame glyph from the handoff (2px, square caps). */
export function ScanIcon({ size = 52, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
      <path d="M3 7V3h4" /><path d="M17 3h4v4" /><path d="M21 17v4h-4" /><path d="M7 21H3v-4" /><path d="M7 12h10" />
    </svg>
  );
}

/** Downloads text as a file from the browser (EXPORT). */
export function downloadText(filename, text, mime = 'text/csv;charset=utf-8') {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([text], { type: mime });
  const url = (window.URL || window.webkitURL).createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => (window.URL || window.webkitURL).revokeObjectURL(url), 0);
}

export const slugFile = (s) => String(s || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'event';
